import { FC, useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";

import { SpeedScreenFormProvider, useSpeed } from "../../../../../context/speed-context";
import {
  TimePanelChart,
  TIME_PANEL_CHART_MIN_HEIGHT,
} from "../../multi-analysis/components/time-panel/time-panel-chart";
import { useTimePanelMarkSelection } from "../../multi-analysis/hooks/use-time-panel-mark-selection";
import type { IMultiAnalysisTimeRange } from "../../multi-analysis/types";
import { getSpeedControlParameterKey, useSpeedControlParameter } from "../hooks/use-speed-control-parameter";
import { useSpeedDataMarks } from "../hooks/use-speed-data-marks";
import type { SpeedScreenForm } from "../hooks/use-speed-screen-forms";
import type { ISpeedScreenFormStrategy, ISpeedScreenFormTimePanelProps } from "../screen-forms/types";

const DefaultMarksBridge: FC<{
  trainId: number;
  globalRange: IMultiAnalysisTimeRange;
}> = ({ trainId, globalRange }) => {
  const { selectedPoints, setDataMarks, setDataMarksError, setDataMarksLoading } = useSpeed();
  const { dataMarks, isLoading } = useSpeedDataMarks(trainId, selectedPoints, globalRange);

  useEffect(() => {
    setDataMarks(dataMarks);
    setDataMarksError(false);
  }, [dataMarks, setDataMarks, setDataMarksError]);

  useEffect(() => {
    setDataMarksLoading(isLoading);
    return () => setDataMarksLoading(false);
  }, [isLoading, setDataMarksLoading]);

  return null;
};

const FormTimePanelBridge: FC<
  Omit<ISpeedScreenFormTimePanelProps, "onLoadingChange"> & {
    strategy: ISpeedScreenFormStrategy;
  }
> = ({ strategy, ...props }) => {
  const { setDataMarksLoading } = useSpeed();

  return <>{strategy.renderTimePanel?.({ ...props, onLoadingChange: setDataMarksLoading })}</>;
};

const SpeedTimePanelChart: FC<
  Omit<ISpeedScreenFormTimePanelProps, "onLoadingChange"> & {
    strategy: ISpeedScreenFormStrategy | null;
    isLoadingTrend: boolean;
  }
> = ({ globalRange, selectedParameter, trendSeries, strategy, isLoadingTrend }) => {
  const {
    selectedPoints,
    frameRange,
    setFrameRange,
    dataMarks,
    selectedDataMarks,
    setSelectedDataMarks,
    isDataMarksLoading,
  } = useSpeed();
  const { handleMarkClick } = useTimePanelMarkSelection({
    dataMarks,
    frameRange,
    selectedDataMarks,
    setSelectedDataMarks,
    additivePlainClick: strategy?.id === "orbit",
    singleSelection: strategy?.id === "bode" || strategy?.id === "cascade",
  });
  const selectedMarkKeys = useMemo(() => new Set(selectedDataMarks.map(mark => mark.key)), [selectedDataMarks]);

  return (
    <ProgressOverlay
      isLoading={isLoadingTrend || isDataMarksLoading}
      wrapperSx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto" }}
    >
      <Box sx={{ height: "100%", minHeight: TIME_PANEL_CHART_MIN_HEIGHT }}>
        <TimePanelChart
          globalRange={globalRange}
          frameRange={frameRange}
          trendSeries={trendSeries}
          dataMarks={dataMarks}
          dataMarkPlacement="top"
          groupOverlappingMarks
          dataMarkSelectionHintId={
            strategy?.id === "bode" || strategy?.id === "cascade"
              ? "pages.multiAnalysis.timePanel.selectDataMark"
              : undefined
          }
          selectedMarkKeys={selectedMarkKeys}
          hasSelectedPoints={selectedPoints.length > 0 || selectedParameter !== null}
          onFrameRangeChange={setFrameRange}
          onFrameRangeReset={() => setFrameRange(globalRange)}
          onMarkClick={handleMarkClick}
          renderMarkTooltip={
            strategy?.renderDataMarkTooltip ? mark => strategy.renderDataMarkTooltip?.(mark) ?? null : undefined
          }
        />
      </Box>
    </ProgressOverlay>
  );
};

export const resolveSpeedTimePanelStrategy = (
  placedStrategies: readonly ISpeedScreenFormStrategy[],
  activeFormId: string | null | undefined,
): ISpeedScreenFormStrategy | null => {
  const activeStrategy = placedStrategies.find(strategy => strategy.id === activeFormId);
  return activeStrategy?.renderTimePanel
    ? activeStrategy
    : placedStrategies.find(strategy => strategy.renderTimePanel) ?? null;
};

export const SpeedTimePanel: FC<{
  trainId: number;
  globalRange: IMultiAnalysisTimeRange;
  strategies: readonly ISpeedScreenFormStrategy[];
  activeFormId: string | null;
  placedFormIds: readonly SpeedScreenForm[];
}> = ({ trainId, globalRange, strategies, activeFormId, placedFormIds }) => {
  const { formatMessage } = useIntl();
  const { parameters, selectedParameter, selectParameter, trendSeries, isLoadingParameters, isLoadingTrend } =
    useSpeedControlParameter(trainId, globalRange);
  const placedStrategies = Array.from(new Set(placedFormIds))
    .map(formId => strategies.find(strategy => strategy.id === formId))
    .filter((strategy): strategy is ISpeedScreenFormStrategy => strategy !== undefined);
  const timePanelStrategy = resolveSpeedTimePanelStrategy(placedStrategies, activeFormId);
  const controlUnits =
    selectedParameter?.unitLabel ||
    (selectedParameter?.units != null ? formatMessage({ id: `enums.units.${selectedParameter.units}` }) : "");
  const timePanelProps = { trainId, globalRange, selectedParameter, controlUnits, trendSeries };
  const chart = (
    <SpeedTimePanelChart {...timePanelProps} strategy={timePanelStrategy} isLoadingTrend={isLoadingTrend} />
  );

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex" }}>
      {placedStrategies
        .filter(strategy => strategy.renderTimePanel)
        .map(strategy => (
          <SpeedScreenFormProvider key={strategy.id} formId={strategy.id}>
            <FormTimePanelBridge {...timePanelProps} strategy={strategy} />
          </SpeedScreenFormProvider>
        ))}
      {timePanelStrategy ? (
        <SpeedScreenFormProvider key={timePanelStrategy.id} formId={timePanelStrategy.id}>
          {chart}
        </SpeedScreenFormProvider>
      ) : (
        <>
          <DefaultMarksBridge trainId={trainId} globalRange={globalRange} />
          {chart}
        </>
      )}
      <Box
        sx={{
          flex: "0 0 auto",
          width: { xs: 180, lg: 240 },
          p: 1.5,
          borderLeft: "1px solid #d8ddea",
          overflowY: "auto",
        }}
      >
        <FormControl fullWidth size="small" disabled={isLoadingParameters || !parameters.length}>
          <InputLabel id="speed-control-parameter-label" shrink>
            <FormattedMessage id="pages.speed.controlParameter" />
          </InputLabel>
          <Select
            labelId="speed-control-parameter-label"
            notched
            value={selectedParameter ? getSpeedControlParameterKey(selectedParameter) : ""}
            label={formatMessage({ id: "pages.speed.controlParameter" })}
            onChange={event => selectParameter(event.target.value)}
          >
            {parameters.map(parameter => (
              <MenuItem
                key={getSpeedControlParameterKey(parameter)}
                value={getSpeedControlParameterKey(parameter)}
                title={parameter.description ?? undefined}
              >
                {parameter.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};
